import { Link, router } from "@inertiajs/react"
import {
  BookOpen,
  Clock,
  FileText,
  Loader2,
  Plus,
  Sparkles,
  Target,
  Type,
  X,
} from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { useDebouncedCallback } from "use-debounce"

import consumer from "@/channels/consumer"
import { AudioShimmer } from "@/components/flashcards/audio-shimmer"
import { JustCreatedBanner } from "@/components/flashcards/just-created-banner"
import { RelatedCards } from "@/components/flashcards/related-cards"
import { SearchFilterBar } from "@/components/flashcards/search-filter-bar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { useFlashcardChannel } from "@/hooks/use-flashcard-channel"
import { useFlashcardProps } from "@/hooks/use-flashcard-props"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { Flashcard, FlashcardsFilters } from "@/types"

interface Props {
  flashcards: Flashcard[]
  dueCount: number
  filters?: FlashcardsFilters
  studyImportId?: string
  studyImportTotal?: number
}

export default function FlashcardsIndex(props: unknown) {
  const data = useFlashcardProps<Props>(props)
  const serverFlashcards = data.flashcards ?? []
  const dueCount = data.dueCount ?? 0
  const filters = data.filters
  const studyImportId = data.studyImportId
  const studyImportTotal = data.studyImportTotal ?? 0

  // Real-time flashcard creation tracking for study imports
  const [createdCards, setCreatedCards] = useState<Flashcard[]>([])

  // Subscribe to FlashcardChannel for flashcard_created events (study import)
  useEffect(() => {
    if (!studyImportId) return

    const subscription = consumer.subscriptions.create(
      { channel: "FlashcardChannel" },
      {
        received(message: {
          type: string
          flashcard: Flashcard
          study_import_id?: string
        }) {
          if (
            message.type === "flashcard_created" &&
            message.study_import_id === studyImportId
          ) {
            setCreatedCards((prev) => [...prev, message.flashcard])
          }
        },
      },
    )

    return () => subscription.unsubscribe()
  }, [studyImportId])

  // Track ActionCable updates separately to merge with server data
  const [audioUpdates, setAudioUpdates] = useState<
    Record<string, Partial<Flashcard>>
  >({})

  // Reset audio updates when server data changes (navigation, filters)
  useEffect(() => {
    setAudioUpdates({})
  }, [serverFlashcards])

  // FIX: Inertia stale data on browser back navigation
  // Problem: Inertia caches page data in browser history. When user navigates
  // back (browser back button), cached data may show stale audioStatus="pending"
  // even though audio has been generated (audioStatus="ready" in DB).
  //
  // Solution: Use Inertia's router.on("navigate") to detect back navigation,
  // then trigger partial reload if any cards still show pending status.
  // Only reloads ["flashcards"] prop to minimize server load.
  //
  // Reference: https://inertiajs.com/manual-visits#navigate-event
  useEffect(() => {
    const removeListener = router.on("navigate", () => {
      const hasPendingAudio = serverFlashcards.some(
        (card) => card.audioStatus === "pending",
      )
      if (hasPendingAudio) {
        // Defer reload to next tick to allow navigation state to settle
        setTimeout(() => {
          router.reload({ only: ["flashcards"] })
        }, 0)
      }
    })

    return () => removeListener()
  }, [serverFlashcards])

  // Subscribe to FlashcardChannel for real-time audio updates
  useFlashcardChannel((updatedCard) => {
    setAudioUpdates((prev) => ({
      ...prev,
      [updatedCard.uuid]: updatedCard,
    }))
  })

  // Merge server data with ActionCable updates + real-time created cards
  const serverMerged = serverFlashcards.map((card) => ({
    ...card,
    ...(audioUpdates[card.uuid] || {}),
  }))

  // Prepend real-time created cards (dedupe against server data)
  const serverUuids = new Set(serverMerged.map((c) => c.uuid))
  const newCards = createdCards.filter((c) => !serverUuids.has(c.uuid))
  const flashcards = [...newCards, ...serverMerged]

  const importProgress =
    studyImportId && studyImportTotal > 0
      ? Math.round(
          ((serverFlashcards.length + newCards.length) / studyImportTotal) * 100,
        )
      : null

  // Local state for controlled inputs
  const [search, setSearch] = useState(filters?.search ?? "")
  const [type, setType] = useState<string>(filters?.type ?? "all")
  const [status, setStatus] = useState<string>(filters?.status ?? "all")
  const [sort, setSort] = useState<string>(filters?.sort ?? "created_desc")

  // Check if we're showing just-created cards
  const isJustCreated = filters?.created && filters.created.length > 0
  const justCreatedCount = filters?.created?.length ?? 0

  // Build query params helper
  const buildQueryParams = useCallback(
    (overrides: Partial<FlashcardsFilters> = {}) => {
      const params: Record<string, string | undefined> = {
        q: overrides.search ?? search,
        type: overrides.type ?? type,
        status: overrides.status ?? status,
        sort: overrides.sort ?? sort,
      }

      // Remove default/empty values
      if (params.q === "") delete params.q
      if (params.type === "all") delete params.type
      if (params.status === "all") delete params.status
      if (params.sort === "created_desc") delete params.sort

      return params
    },
    [search, type, status, sort],
  )

  // Navigate with filters
  const navigateWithFilters = useCallback(
    (params: Record<string, string | undefined>) => {
      router.visit("/flashcards", {
        data: params as Record<string, string>,
        preserveState: true,
        preserveScroll: true,
      })
    },
    [],
  )

  // Debounced search - only trigger at 3+ chars or empty (to clear)
  // 150ms debounce to keep total response under 350ms
  const debouncedSearch = useDebouncedCallback((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      navigateWithFilters(buildQueryParams({ search: value }))
    }
  }, 150)

  // Handle filter changes
  const handleSearchChange = (value: string) => {
    setSearch(value)
    debouncedSearch(value)
  }

  const handleTypeChange = (value: string) => {
    setType(value)
    navigateWithFilters(buildQueryParams({ type: value }))
  }

  const handleStatusChange = (value: string) => {
    setStatus(value)
    navigateWithFilters(buildQueryParams({ status: value }))
  }

  const handleSortChange = (value: string) => {
    setSort(value)
    navigateWithFilters(buildQueryParams({ sort: value }))
  }

  const handleClearJustCreated = () => {
    // Navigate without the created param
    navigateWithFilters(buildQueryParams())
  }

  const formatNextReview = (nextReviewAt: string | undefined) => {
    if (!nextReviewAt) return "Start practicing"

    const date = new Date(nextReviewAt)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return "Review now"
    if (diffDays === 0) return "Review today"
    if (diffDays === 1) return "Tomorrow"
    if (diffDays < 7) return `In ${diffDays} days`
    if (diffDays < 30) return `In ${Math.ceil(diffDays / 7)} weeks`
    return `In ${Math.ceil(diffDays / 30)} months`
  }

  const getStatusInfo = (flashcard: Flashcard) => {
    // New card - positive green (fresh start!)
    if (!flashcard.nextReviewAt) {
      return {
        label: "New",
        color:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
      }
    }

    const date = new Date(flashcard.nextReviewAt)
    const now = new Date()

    // Overdue - needs attention (red/orange)
    if (date <= now) {
      return {
        label: "Due",
        color:
          "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
      }
    }

    // Scheduled - on track (blue)
    return {
      label: "Scheduled",
      color: "bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300",
    }
  }

  const getCardTypeBadge = (cardType: string | undefined) => {
    if (!cardType || cardType === "template") return null

    if (cardType === "phrase") {
      return (
        <Badge
          variant="outline"
          className="gap-1 border-violet-300 bg-violet-50 text-xs text-violet-700 dark:border-violet-700 dark:bg-violet-950 dark:text-violet-300"
        >
          <FileText className="size-3" />
          Phrase
        </Badge>
      )
    }

    if (cardType === "word") {
      return (
        <Badge
          variant="outline"
          className="gap-1 border-fuchsia-300 bg-fuchsia-50 text-xs text-fuchsia-700 dark:border-fuchsia-700 dark:bg-fuchsia-950 dark:text-fuchsia-300"
        >
          <Type className="size-3" />
          Word
        </Badge>
      )
    }

    return null
  }

  // Check if any filters are active
  const hasActiveFilters =
    search !== "" ||
    type !== "all" ||
    status !== "all" ||
    sort !== "created_desc"

  return (
    <AppLayout breadcrumbs={[{ title: "Flashcards", href: "/flashcards" }]}>
      <div className="space-y-6 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              My Flashcards
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Practice with spaced repetition
            </p>
          </div>
          <Link href="/conversations?hint=flashcard">
            <Button>
              <Plus className="mr-2 size-4" />
              Create New
            </Button>
          </Link>
        </div>

        {/* Search & Filters */}
        <SearchFilterBar
          search={search}
          type={type}
          status={status}
          sort={sort}
          onSearchChange={handleSearchChange}
          onTypeChange={handleTypeChange}
          onStatusChange={handleStatusChange}
          onSortChange={handleSortChange}
        />

        {/* Just Created Banner - below filters, contextual to results */}
        {isJustCreated && (
          <JustCreatedBanner
            count={justCreatedCount}
            onClear={handleClearJustCreated}
          />
        )}

        {/* Study Import Progress Banner */}
        {studyImportId && (
          <div className="animate-in fade-in slide-in-from-top-2 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-50/50 p-4 dark:border-amber-800/40 dark:from-amber-950/20 dark:to-amber-950/10">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/40">
                <BookOpen className="size-4 text-amber-700 dark:text-amber-400" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {importProgress !== null && importProgress < 100 ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="size-3.5 animate-spin text-amber-600" />
                        Creating flashcards from your study material...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Sparkles className="size-3.5 text-amber-600" />
                        Flashcards from study import
                      </span>
                    )}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => router.visit("/flashcards")}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
                {importProgress !== null && studyImportTotal > 0 && (
                  <div className="space-y-1">
                    <Progress
                      value={importProgress}
                      className="h-1.5"
                    />
                    <p className="text-xs text-slate-500">
                      {serverFlashcards.length + newCards.length} of{" "}
                      {studyImportTotal} cards created
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards - only show when not filtering */}
        {!isJustCreated && !hasActiveFilters && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="flex items-center gap-4 p-6">
                <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-900">
                  <Sparkles className="size-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Total Cards
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {flashcards.length}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-4 p-6">
                <div className="rounded-full bg-red-100 p-3 dark:bg-red-900">
                  <Clock className="size-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Due for Review
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {dueCount}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-center gap-4 p-6">
                <div className="rounded-full bg-green-100 p-3 dark:bg-green-900">
                  <Target className="size-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Mastered
                  </p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {
                      flashcards.filter(
                        (f) => f.easeFactor >= 2.5 && f.repetitions >= 3,
                      ).length
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Practice Button */}
        {dueCount > 0 && !isJustCreated && (
          <Link href="/flashcards/due">
            <Button size="lg" className="w-full">
              <Clock className="mr-2 size-5" />
              Practice {dueCount} Due Card{dueCount !== 1 ? "s" : ""}
            </Button>
          </Link>
        )}

        {/* Flashcards List */}
        {flashcards.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Sparkles className="mb-4 size-12 text-slate-400" />
              <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
                {hasActiveFilters
                  ? "No matching flashcards"
                  : "No flashcards yet"}
              </h3>
              <p className="mb-6 text-center text-sm text-slate-600 dark:text-slate-400">
                {hasActiveFilters
                  ? "Try adjusting your search or filters"
                  : "Create your first flashcard from a conversation message"}
              </p>
              {hasActiveFilters ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch("")
                    setType("all")
                    setStatus("all")
                    setSort("created_desc")
                    router.visit("/flashcards")
                  }}
                >
                  Clear Filters
                </Button>
              ) : (
                <Link href="/conversations">
                  <Button>
                    <Plus className="mr-2 size-4" />
                    Browse Conversations
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {isJustCreated
                  ? `Just Created (${flashcards.length})`
                  : `${hasActiveFilters ? "Filtered" : "All"} Cards (${flashcards.length})`}
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {flashcards.map((flashcard) => {
                const statusInfo = getStatusInfo(flashcard)
                return (
                  <Link
                    key={flashcard.id}
                    href={`/flashcards/${flashcard.uuid}`}
                  >
                    <Card className="transition-shadow hover:shadow-md">
                      <CardHeader>
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="line-clamp-2 text-base">
                            {flashcard.frontText}
                          </CardTitle>
                          <div className="flex shrink-0 items-center gap-1.5">
                            {getCardTypeBadge(flashcard.cardType)}
                            <Badge
                              variant="secondary"
                              className={cn("shrink-0", statusInfo.color)}
                            >
                              {statusInfo.label}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {flashcard.backRomanization && (
                          <p className="line-clamp-2 text-sm font-medium text-slate-900 dark:text-white">
                            {flashcard.backRomanization}
                          </p>
                        )}

                        <div className="line-clamp-1 text-xs text-slate-500 dark:text-slate-500">
                          {flashcard.backText}
                        </div>

                        {/* Audio status indicator */}
                        {(flashcard.audioStatus === "pending" ||
                          flashcard.audioStatus === "failed") && (
                          <div onClick={(e) => e.preventDefault()}>
                            <AudioShimmer
                              status={flashcard.audioStatus}
                              variant="compact"
                            />
                          </div>
                        )}

                        {/* Related cards pills */}
                        {flashcard.wordFlashcards &&
                          flashcard.wordFlashcards.length > 0 && (
                            <div onClick={(e) => e.preventDefault()}>
                              <RelatedCards
                                cards={flashcard.wordFlashcards}
                                direction="words"
                                maxVisible={3}
                              />
                            </div>
                          )}
                        {flashcard.phraseFlashcards &&
                          flashcard.phraseFlashcards.length > 0 && (
                            <div onClick={(e) => e.preventDefault()}>
                              <RelatedCards
                                cards={flashcard.phraseFlashcards}
                                direction="phrases"
                                maxVisible={3}
                              />
                            </div>
                          )}

                        <div className="flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-700">
                          <span className="text-xs text-slate-600 dark:text-slate-400">
                            {formatNextReview(flashcard.nextReviewAt)}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-500">
                            {flashcard.repetitions}x reviewed
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
